import { Inject, Injectable, Optional } from '@angular/core';
import {
  DATE_FORMAT_KEY,
  DEFAULT_TRIP_VIEW,
  FLEET_KEY,
  FLEETMANAGER,
  LOGIN_INFO,
  METRIC_UNIT_KEY,
  TIMEZONE_KEY,
  TRIP_DETAILS_VIEW_TYPE_KEY,
  LANGUAGE_KEY,
  THEME_KEY,
  AWS_COGNITO_REGION_KEY,
  USER_COUNTRY_KEY,
  COOKIES_CONSENT_KEY,
  TABLE_PAGESIZE_KEY,
  DATE_FORMAT_READABLE_KEY,
  PINNED_REPORTS_KEY,
  LANDING_PAGE_LANGUAGE_KEY,
  DRIVER_CENTRIC,
  USER_OVERRIDDEN_SETTINGS_KEY,
} from '@app-core/constants/constants';
import { TripDetailsViewType } from '@app-core/models/access.model';
import { CookieConsent, LoginInfo, UserOverriddenSettings } from '@app-core/models/core.model';
import { StorageService } from '@app-core/services/storage/storage.service';
import { environment } from '@env/environment';
import { GEOTAB_DATABASE } from 'src/app/geotab/geotab-utils';

@Injectable({
  providedIn: 'root',
})
export class AccessService {
  constructor(private _storage: StorageService, @Optional() @Inject(GEOTAB_DATABASE) private geotabDatabase: string) {}

  public setLoginInfo(info: LoginInfo) {
    let key = LOGIN_INFO;
    if (Boolean(environment.isGeotabEnv)) key = key.concat(`:${this.geotabDatabase}`);
    this._storage.setStorageValue(key, info);
    this.tripDetailsViewType = DEFAULT_TRIP_VIEW;
  }

  public set tripDetailsViewType(viewType: TripDetailsViewType) {
    this._storage.setStorageValue(TRIP_DETAILS_VIEW_TYPE_KEY, viewType);
  }

  public get tripDetailsViewType(): TripDetailsViewType {
    return this._storage.getStorageValue(TRIP_DETAILS_VIEW_TYPE_KEY);
  }

  public getUserIdOrHash() {
    return this.getLoginInfo().userId ? this.getLoginInfo().userId : this.getLoginInfo().userHash;
  }
  public set currentCookiesConsent(consent: CookieConsent) {
    this._storage.setStorageValue(COOKIES_CONSENT_KEY, consent);
  }

  public get currentCookiesConsent() {
    return this._storage.getStorageValue(COOKIES_CONSENT_KEY);
  }

  public set currentTimeZone(timeZone: string) {
    this._storage.setStorageValue(TIMEZONE_KEY, timeZone);
  }

  public get currentTimeZone() {
    return this._storage.getStorageValue(TIMEZONE_KEY);
  }

  public set currentMetricUnit(metricUnit: string) {
    this._storage.setStorageValue(METRIC_UNIT_KEY, metricUnit);
  }

  public get currentMetricUnit() {
    return this._storage.getStorageValue(METRIC_UNIT_KEY);
  }

  public set currentDateFormat(dateFormat: string) {
    this._storage.setStorageValue(DATE_FORMAT_KEY, dateFormat);
  }

  public get currentDateFormat() {
    return this._storage.getStorageValue(DATE_FORMAT_KEY);
  }

  public set isReadableDateFormat(isReadableDateFormat: boolean) {
    this._storage.setStorageValue(DATE_FORMAT_READABLE_KEY, isReadableDateFormat);
  }

  public get isReadableDateFormat() {
    return this._storage.getStorageValue(DATE_FORMAT_READABLE_KEY);
  }

  public set isDriverCentric(isDriverCentric: boolean) {
    this._storage.setStorageValue(DRIVER_CENTRIC, isDriverCentric);
  }

  public get isDriverCentric() {
    return this._storage.getStorageValue(DRIVER_CENTRIC);
  }

  public set currentFleet(fleetId: string) {
    this._storage.setStorageValue(FLEET_KEY, fleetId);
  }

  public get currentFleet() {
    return this._storage.getStorageValue(FLEET_KEY);
  }

  public set currentLanguage(language: string) {
    this._storage.setStorageValue(LANGUAGE_KEY, language);
    this.landingPageLanguage = language;
  }

  public get currentLanguage() {
    return this._storage.getStorageValue(LANGUAGE_KEY);
  }

  private getUserOverriddenSettingsMap(): Record<string, UserOverriddenSettings> {
    return this._storage.getStorageValue(USER_OVERRIDDEN_SETTINGS_KEY) || {};
  }

  private setUserOverriddenSetting(field: keyof UserOverriddenSettings, value: boolean) {
    const userId = this.getUserIdOrHash();
    if (!userId) return;
    const settingsMap = this.getUserOverriddenSettingsMap();
    settingsMap[userId] = { ...settingsMap[userId], [field]: value };
    this._storage.setStorageValue(USER_OVERRIDDEN_SETTINGS_KEY, settingsMap);
  }

  private getUserOverriddenSetting(field: keyof UserOverriddenSettings): boolean {
    const userId = this.getUserIdOrHash();
    return Boolean(userId && this.getUserOverriddenSettingsMap()[userId]?.[field]);
  }

  public set isLanguageUserOverridden(value: boolean) {
    this.setUserOverriddenSetting('language', value);
  }

  public get isLanguageUserOverridden() {
    return this.getUserOverriddenSetting('language');
  }

  public set isTimeZoneUserOverridden(value: boolean) {
    this.setUserOverriddenSetting('timeZone', value);
  }

  public get isTimeZoneUserOverridden() {
    return this.getUserOverriddenSetting('timeZone');
  }

  public set isDateFormatUserOverridden(value: boolean) {
    this.setUserOverriddenSetting('dateFormat', value);
  }

  public get isDateFormatUserOverridden() {
    return this.getUserOverriddenSetting('dateFormat');
  }

  public set isMetricUnitUserOverridden(value: boolean) {
    this.setUserOverriddenSetting('metricUnit', value);
  }

  public get isMetricUnitUserOverridden() {
    return this.getUserOverriddenSetting('metricUnit');
  }

  public set currentTheme(theme: string) {
    this._storage.setStorageValue(THEME_KEY, theme);
  }

  public get currentTheme() {
    return this._storage.getStorageValue(THEME_KEY);
  }
  public set currentTablePageSize(tablePageSize: number) {
    this._storage.setStorageValue(TABLE_PAGESIZE_KEY, tablePageSize);
  }

  public get currentTablePageSize() {
    return this._storage.getStorageValue(TABLE_PAGESIZE_KEY);
  }

  public set currentPinnedReports(pinnedReports: string[]) {
    this._storage.setStorageValue(PINNED_REPORTS_KEY, pinnedReports);
  }

  public get currentPinnedReports() {
    return this._storage.getStorageValue(PINNED_REPORTS_KEY);
  }

  public set landingPageLanguage(language: string) {
    this._storage.setStorageValue(LANDING_PAGE_LANGUAGE_KEY, language);
  }

  public get landingPageLanguage() {
    return this._storage.getStorageValue(LANDING_PAGE_LANGUAGE_KEY);
  }

  public getLoginInfo(): LoginInfo {
    let key = LOGIN_INFO;
    if (Boolean(environment.isGeotabEnv)) key = key.concat(`:${this.geotabDatabase}`);
    return this._storage.getStorageValue(key) || {};
  }

  public getUserPermissions(): string[] {
    const { fleets = [] } = this.getLoginInfo();
    const currentFleet = fleets.length && fleets.filter((x: any) => x.fleetId === this.currentFleet)[0];
    return currentFleet?.role?.permissions || [];
  }

  public isAdminRole(): boolean {
    const { fleets = [] } = this.getLoginInfo();
    const currentFleet = fleets.length && fleets.filter((x: any) => x.fleetId === this.currentFleet)[0];
    return currentFleet?.role?.isAdmin || false;
  }

  public isUserAuthenticated(): boolean {
    return this.hasValidToken();
  }

  public isLoggedIn() {
    const loginInfo = this.getLoginInfo();
    return loginInfo && Object.entries(loginInfo).length ? true : false;
  }

  public isFleetManager(): boolean {
    const { userType = '' } = this.getLoginInfo();
    return userType === FLEETMANAGER;
  }

  public hasValidToken(): boolean {
    // Note: Not important to decrypt token and use the value
    // Even if the value is manipulated the user will still be logged in but can't make any API requests - 401
    const { tokenExpiresAt = 0 } = this.getLoginInfo() || {};
    return tokenExpiresAt > Date.now();
  }

  public clearLocalStorage() {
    this._storage.clearAll();
  }

  public removeLoginInfo() {
    this._storage.removeStorageValue(LOGIN_INFO);
  }

  public getAwsCognitoRegion(): string {
    const defaultAwsCognitoRegion = Object.keys(environment.cognitoConfigs)[0];
    return this._storage.getStorageValue(AWS_COGNITO_REGION_KEY) || defaultAwsCognitoRegion;
  }

  public getUserCountry(): string {
    return this._storage.getStorageValue(USER_COUNTRY_KEY);
  }
}
